// src/features/privateAlbum/utils/privateAlbumHelpers.js

import { ALBUM_STATUS, ACCESS_STATUS } from "../types/privateAlbumTypes";

/**
 * Generate a unique album code
 */
export const generateAlbumCode = (title, clientName) => {
  const prefix = title?.substring(0, 3).toUpperCase() || "ALB";
  const clientPrefix = clientName?.substring(0, 2).toUpperCase() || "CL";
  const timestamp = Date.now().toString(36).substring(-6).toUpperCase();
  return `${prefix}-${clientPrefix}-${timestamp}`;
};

/**
 * Generate a unique access token
 */
export const generateAccessToken = () => {
  return (
    Math.random().toString(36).substring(2, 15) +
    Math.random().toString(36).substring(2, 15)
  );
};

/**
 * Format file size
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

/**
 * Get role permissions
 */
export const getRolePermissions = (role) => {
  const permissions = {
    viewer: { view: true, download: false, comment: false, upload: false },
    commenter: { view: true, download: false, comment: true, upload: false },
    downloader: { view: true, download: true, comment: false, upload: false },
    uploader: { view: true, download: false, comment: false, upload: true },
  };
  return permissions[role] || permissions.viewer;
};

/**
 * Validate email
 */
export const isValidEmail = (email) => {
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return regex.test(email);
};

/**
 * Validate album data
 */
export const validateAlbumData = (data) => {
  const errors = {};

  if (!data.title?.trim()) {
    errors.title = "Title is required";
  }
  if (!data.eventType) {
    errors.eventType = "Event type is required";
  }
  if (!data.eventDate) {
    errors.eventDate = "Event date is required";
  }
  if (!data.clientEmail?.trim()) {
    errors.clientEmail = "Client email is required";
  } else if (!isValidEmail(data.clientEmail)) {
    errors.clientEmail = "Invalid email format";
  }

  return errors;
};

/**
 * Check if album is expired
 */
export const isAlbumExpired = (album) => {
  if (!album.expiresAt) return false;
  return new Date(album.expiresAt) < new Date();
};

/**
 * Check if album is accessible
 */
export const isAlbumAccessible = (album) => {
  if (!album) return false;
  if (album.status === ALBUM_STATUS.DELETED) return false;
  if (album.status === ALBUM_STATUS.ARCHIVED) return false;
  if (isAlbumExpired(album)) return false;
  return true;
};

/**
 * Sort photos by order
 */
export const sortPhotosByOrder = (photos) => {
  return [...photos].sort((a, b) => (a.order || 0) - (b.order || 0));
};

/**
 * Get access status display
 */
export const getAccessStatusDisplay = (status) => {
  const statusMap = {
    pending: { label: "Pending", color: "warning" },
    accepted: { label: "Accepted", color: "success" },
    revoked: { label: "Revoked", color: "danger" },
    expired: { label: "Expired", color: "secondary" },
  };
  return statusMap[status] || { label: status, color: "secondary" };
};

/**
 * Get album status display
 */
export const getAlbumStatusDisplay = (status) => {
  const statusMap = {
    active: { label: "Active", color: "success" },
    archived: { label: "Archived", color: "warning" },
    deleted: { label: "Deleted", color: "danger" },
  };
  return statusMap[status] || { label: status, color: "secondary" };
};

/**
 * Format date
 */
export const formatDate = (date) => {
  if (!date) return "N/A";
  const d = new Date(date);
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

/**
 * Format date time
 */
export const formatDateTime = (date) => {
  if (!date) return "N/A";
  const d = new Date(date);
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};
